import React, { useState } from "react";
import Dictionary from "../../../utils/Dictionary";
import dictionaryData from "../../../../constants/Data";

const Article = () => {
  const [hoveredWord, setHoveredWord] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseOver = (e) => {
    const word = e.target.innerText;
    const definition = dictionaryData[word];

    if (definition) {
      setHoveredWord({ word, definition });
      setPosition({ x: e.clientX, y: e.clientY });
    } else {
      setHoveredWord(null);
    }
  };

  const handleMouseOut = () => {
    setHoveredWord(null);
  };
  return (
    <div>
      {hoveredWord && (
        <Dictionary
          word={hoveredWord.word}
          definition={hoveredWord.definition}
          position={position}
        />
      )}
      <div>
        <h2 id="engineering-overview_o">Engineering overview</h2>
        <hr className="dividergeneral mb-5"></hr>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          The aim of our project is to tackle <span>cadmium</span> ion{" "}
          <span>bioaccumulation</span> in the human body<a href="#reference1">
            <sup>1</sup>
          </a>{" "}. To meet our needs the
          engineered system must fulfill several requirements and constraints.
          The first requirement to be met is{" "}
          <strong>catching cadmium ions</strong>. This interaction should be{" "}
          <strong>highly</strong> <span>specific</span> to avoid depleting
          essential <span>bivalent cations</span> such as zinc, calcium, and
          magnesium. The solution is the synthetic <span>phytochelatin</span>{" "}
          EC20 <a href="#reference2">
            <sup>2</sup>
          </a>{" "}.
        </p>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          The second constraint is catching cadmium before it is absorbed{" "}
          <strong>in the small intestine</strong>. To bring our phytochelatin to
          its place of action we use a <span>Live Biotherapeutic Product</span>{" "}
          (LBP). Consequently, this implies bringing EC20{" "}
          <strong>to the surface of our LBP</strong>. To achieve that, we are
          using <span>Lpp-OmpA</span>, a well characterized surface display
          system in bacteria.
        </p>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          Although the LBP must have limited capacities to divide in the small
          intestine to increase safety, it must{" "}
          <strong>stay long enough to mediate cadmium catching</strong>. To do
          so we are adding an anchor to our LBP, SpaC <a href="#reference3">
            <sup>3</sup>
          </a>{" "}, a protein able to bind to
          the intestinal <span>mucus</span> to moor our LBP to the intestinal
          lining.
        </p>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          However, once bound to cadmium,{" "}
          <strong>the LBP needs to be expelled from the body</strong> in order
          to eliminate the cadmium. In this way, the production by the LBP of
          SpaC - the mucus anchor - needs to be stopped after the LBP production
          in laboratory. The solution is to couple an{" "}
          <span>IPTG inducible Lac operator system</span> with a theophylline{" "}
          <span>riboswitch</span> <a href="#reference4">
            <sup>4</sup>
          </a>{" "} to control <span>transcription</span> and{" "}
          <span>translation</span> of SpaC, respectively.
        </p>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          Acting in the small intestine implies contact with the host{" "}
          <span>microbiota</span>. We want to{" "}
          <strong>limit the LBP - microbiota interaction</strong> as much as
          possible to avoid microbiota disruption. Our answer to this constraint
          is to impair <span>Horizontal Gene Transfer</span> by deleting the{" "}
          <span>DUT</span> and <span>UNG</span> <a href="#reference5">
            <sup>5</sup>
          </a>{" "} genes in our{" "}
          <span>chassis</span>.
        </p>
        <br />
        <table className="mb-20">
          <tr>
            <th>Aim</th>
            <td>Tackling cadmium ion bioaccummulation in the human body</td>
          </tr>
          <tr>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <th>Requirement / Constraint</th>
            <th>Solution</th>
          </tr>
          <tr>
            <td>Catching cadmium ions specifically EC20</td>
            <td>EC20</td>
          </tr>
          <tr>
            <td>In the small intestine</td>
            <td>A Live Biotherapeutic product</td>
          </tr>
          <tr>
            <td>To the LBT surface Lpp-OmpA</td>
            <td>Lpp-OmpA</td>
          </tr>
          <tr>
            <td>Stay long enough to mediate cadmium catching</td>
            <td>SpaC</td>
          </tr>
          <tr>
            <td>The LBP needs to be expelled from the body</td>
            <td>
              Control of SpaC expression with an IPTG inducible Lac operator
              coupled with theophylline riboswitch
            </td>
          </tr>
          <tr>
            <td>Limit the LBP - microbiota interaction</td>
            <td>Dut & Ung double negative chassis</td>
          </tr>
        </table>
        <br />
        <figure className="flex flex-col justify-center items-center gap-4">
          <img
            style={{ width: "60%", height: "60%" }}
            alt="spac"
            id="figure-1-both"
            src="https://static.igem.wiki/teams/4888/wiki/engineering/both.png"
          />
          <figcaption>
            <b>
              <u>Figure 1:</u>{" "}
            </b>
            Bacteria overview
          </figcaption>
        </figure>
        <br />
        <h2 className="mt-20" id="engineering-overview-parts_o">
          Principal parts of interest
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <h3 className="mb-5 mt-10" id="overview-ec20_o">
          EC20
        </h3>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          EC20 is a synthetic <span>phytochelatin</span> with an{" "}
          <span>affinity</span> for cadmium ions that is higher than for other{" "}
          <span>bivalent cations</span> such as zinc. First synthetized by W.
          Bae & al. 2000, it is composed of 20 repetitions of the amino
          acids pair Glutamic acid plus Cysteine, and a terminal Glycine. We use
          its binding capacities to catch and trap cadmium ions at the surface
          of our live biotherapeutic product.
        </p>
        <h3 className="mb-5 mt-10" id="overview-spac_o">
          SpaC
        </h3>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          SpaC is a subunit of the <span>Lactobacillus rhamnosus GG</span>{" "}
          <span>sortase-dependent pili</span>, SpaCBA. Its natural function is
          to adhere to human mucus and collagen<a href="#reference3">
            <sup>3,</sup>
          </a>{" "} <a href="#reference6">
            <sup>6</sup>
          </a>{" "}. We use its binding
          capacities to act as an anchor for our live biotherapeutic product.
        </p>
        <h3 className="mb-5 mt-10" id="overview-lpp-ompa_o">
          Lpp-OmpA
        </h3>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          Lpp-OmpA is a well-known and characterized part of{" "}
          <span>synthetic biology</span>. It is a <span>fusion protein</span> of
          two E. coli proteins:
        </p>
        <br />
        <ul
          className="ml-12 mb-10"
          style={{ listStyleType: "disc", listStylePosition: "inside" }}
        >
          <li>
            The signal sequence and the first nine N-terminal amino acids of the
            major outer membrane lipoprotein, Lpp.
          </li>
          <li>
            The amino acids 46 to 159 of the outer membrane protein A, OmpA.
          </li>
        </ul>
        <br />
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          Localized to the bacterium outer membrane, this part serves as a{" "}
          <span>surface display system</span> for both EC20 and SpaC proteins
          when fused to the respective N terminus of the aformentioned proteins.
        </p>

        <h3 className="mb-5 mt-10" id="overview-IPTG_o">
          IPTG inducible Lac operator + LacI
        </h3>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          The gene expression control system composed of the IPTG inducible Lac
          operator and the LacI inhibitor is a well-known and used transcription
          control system. In absence of IPTG, the inhibitor blocks{" "}
          <span>RNA polymerase</span> binding to the gene <span>promoter</span>{" "}
          and inhibits the gene transcription. We use this IPTG inducible Lac
          operator coupled with a Theophylline riboswitch to control SpaC
          expression and avoid expression <span>leakiness</span>.
        </p>
        <h3 className="mb-5 mt-10" id="overview-theophiline-riboswitch_o">
          Theophylline riboswitch
        </h3>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          The Theophylline riboswitch is a synthetic riboswitch Engineered by
          S.Topp & al. 2010<a href="#reference4">
            <sup>4</sup>
          </a>{" "}. It regulates gene expression at the translation
          level via theophylline-RNA interactions, sequestering the{" "}
          <span>ribosome binding site</span> when Theophylline is absent. We use
          this riboswitch coupled to a IPTG inducible Lac operator to control
          SpaC expression and avoid expression leakiness.
        </p>
        <h3 className="mb-5 mt-10" id="overview-dut-ung_o">
          Dut and Ung genes
        </h3>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          The <span>DUT</span> gene encodes a crucial enzyme involved in
          nucleotide metabolism. This enzyme plays a vital role in converting{" "}
          <span>dUTP</span> into <span>dUMP</span> and pyrophosphate. This{" "}
          <span>enzymatic reaction</span> serves two essential functions in the
          cell: it provides the necessary precursor (dUMP) for the synthesis of
          thymine nucleotides required for <span>DNA replication</span> and
          helps regulate the intracellular levels of dUTP. Elevated dUTP levels
          can result in the increased incorporation of uracil into DNA,
          triggering a complex <span>excision repair process</span> mediated by
          uracil glycosylase.
        </p>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          The UNG gene plays an important role in encoding an enzyme called{" "}
          <span>uracil-DNA glycosylase</span>. One of its essential roles is to
          safeguard the integrity of DNA by removing uracil from DNA molecules.
        </p>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          <span>Negative mutants</span> for those genes are used to impair{" "}
          <span>Horizontal Gene Transfer</span> between our{" "}
          <span>Live Biotherapeutic Product</span> and <span>microbiota</span>’s
           strains.
        </p>
        <h3 className="mb-5 mt-10" id="overview-imp_o">
          Testing implementation
        </h3>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          For the proof of concept, we decided to test separately the cadmium
          catcher protein, EC20, the anchor mucus binding protein, SpaC, and the{" "}
          <span>H.G.T.</span> impaired <span>chassis</span>, DUT/UNG double
          negative mutant. For both EC20 and SpaC, we used the well
          characterized lab strain E. coli BL21 DE3 as it is well suited for
          laboratory manipulations and experiments. For the Dut/Ung{" "}
          <span>double negative mutant</span> we used the competent E. coli
          strain CJ236, a double negative <span>chassis</span> for the genes of
          interest <a href="#reference7">
            <sup>7</sup>
          </a>{" "}.
        </p>
        <p
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          className="mb-2"
        >
          This choice was possible because their functions are independent. It
          was motivated by its simplicity as well as by the fact that those
          three pieces, if operational, could be used by others for their own
          biological machine.
        </p>

        <h2 className="mt-20" id="references_o">
          References
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <ul>
          <ol type="1">
            <li id="reference1">
              {" "}
              Rafati Rahimzadeh M, Rafati Rahimzadeh M, Kazemi S, Moghadamnia
              AA. <br />{" "}
              <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5596182/">
                Cadmium toxicity and treatment: An update. Caspian J Intern Med.
                2017 Summer
              </a>{" "}
              <br />
              ChemBioChem 21.22 (2020): 3255-3265.{" "}
            </li>
            <li id="reference2">
              {" "}
              Bae, Weon, et al. <br />{" "}
              <a href="https://onlinelibrary.wiley.com/doi/epdf/10.1002/1097-0290%2820001205%2970%3A5%3C518%3A%3AAID-BIT6%3E3.0.CO%3B2-5">
                Enhanced bioaccumulation of heavy metals by bacterial cells
                displaying synthetic phytochelatins.
              </a>{" "}
              <br />
              Biotechnology and bioengineering 70.5 (2000): 518-524{" "}
            </li>
            <li id="reference3">
              {" "}
              Kankainen, Matti, et al. <br />{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/19805152/">
                Comparative genomic analysis of Lactobacillus rhamnosus GG
                reveals pili containing a human-mucus binding protein.
              </a>{" "}
              <br />
              Proceedings of the National Academy of Sciences 106.40 (2009):
              17193-17198.{" "}
            </li>
            <li id="reference4">
              {" "}
              Topp, Shana et al. <br />{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/20935124/">
                Synthetic riboswitches that induce gene expression in diverse
                bacterial species.
              </a>{" "}
              <br />
              Applied and environmental microbiology vol. 76,23 (2010): 7881-4.
              doi:10.1128/AEM.01537-10{" "}
            </li>
            <li id="reference5">
              {" "}
              Algar, Elena, et al. <br />{" "}
              <a href="https://www.researchgate.net/publication/342531782_Environmental_Performance_of_Pseudomonas_putida_with_a_Uracylated_Genome">
                {" "}
                Environmental performance of Pseudomonas putida with a
                uracylated genome.{" "}
              </a>{" "}
              <br />
              ChemBioChem 21.22 (2020): 3255-3265.{" "}
            </li>
            <li id="reference6">
              {" "}
              Kant, Abhiruchi, et al. <br />{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/32653644/">
                Crystal structure of lactobacillar SpaC reveals an atypical
                five-domain pilus tip adhesin: Exposing its substrate-binding
                and assembly in SpaCBA pili.
              </a>{" "}
              <br />
              Journal of Structural Biology 211.3 (2020): 107571{" "}
            </li>
            <li id="reference7">
              {" "}
              Lari, Sibghat-Ullah, et al.
              <br />{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/16908222/">
                Quantitative determination of uracil residues in Escherichia
                coli DNA: Contribution of ung, dug, and dut genes to uracil
                avoidance.
              </a>{" "}
              <br />
              DNA repair 5.12 (2006): 1407-1420{" "}
            </li>
          </ol>
        </ul>
      </div>
    </div>
  );
};

export default Article;
